import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, hasDisplayValue } from "../utils/patientUtils";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  SummaryEmpty,
  SummaryLoading,
  SummaryTableWrap,
  summaryTableCell,
  summaryTableHead,
} from "./patientSummaryUi";
import type { SummaryTheme } from "./patientSummaryUi";

interface ExaminationTabProps {
  examinationData: any[];
  loading: boolean;
  theme?: SummaryTheme;
}

type TreatmentRow = {
  key: string;
  item: any;
  treatment: any;
  subTreatment?: any;
  isSub: boolean;
};

const formatTreatmentLabel = (entry: any) =>
  [entry?.treatment, entry?.type, entry?.subtype, entry?.stage].filter(hasDisplayValue).join(" · ");

const formatAmount = (entry: any) => {
  const raw = entry?.price ?? entry?.amount ?? entry?.total;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
};

const formatAmountLabel = (amount: number | null) =>
  amount == null ? "—" : `Rs ${amount.toLocaleString()}`;

const getTreatmentRowKey = (item: any, treatment: any, itemIndex: number, treatmentIndex: number) =>
  `${item?._id ?? itemIndex}-${treatment?._id ?? treatmentIndex}`;

const getUpdatedTime = (entry: any, fallback?: any) => {
  const value = entry?.updatedAt ?? entry?.createdAt ?? fallback?.updatedAt ?? fallback?.createdAt;
  return value ? new Date(value).getTime() : 0;
};

const formatEntryDate = (entry: any) => formatDate(entry?.updatedAt ?? entry?.createdAt);

const StatusBadge: React.FC<{ status: string; theme: SummaryTheme }> = ({ status, theme }) => (
  <span
    className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
      status === "Complete"
        ? theme === "dark"
          ? "bg-emerald-500/20 text-emerald-300"
          : "bg-emerald-100 text-emerald-700"
        : status === "ongoing"
          ? theme === "dark"
            ? "bg-amber-500/20 text-amber-300"
            : "bg-amber-100 text-amber-700"
          : theme === "dark"
            ? "bg-slate-700 text-slate-300"
            : "bg-slate-100 text-slate-600"
    }`}
  >
    {status}
  </span>
);

const ExaminationTab: React.FC<ExaminationTabProps> = ({
  examinationData,
  loading,
  theme = "light",
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const treatmentGroups = useMemo(() => {
    const groups = examinationData.flatMap((item: any, itemIndex: number) =>
      (item?.treatments ?? []).map((treatment: any, treatmentIndex: number) => ({
        rowKey: getTreatmentRowKey(item, treatment, itemIndex, treatmentIndex),
        item,
        treatment,
        subTreatments: Array.isArray(treatment?.subTreatment) ? [...treatment.subTreatment] : [],
      }))
    );

    return groups
      .map((group) => ({
        ...group,
        subTreatments: group.subTreatments.sort(
          (a: any, b: any) => getUpdatedTime(b) - getUpdatedTime(a)
        ),
      }))
      .sort(
        (a, b) => getUpdatedTime(b.treatment, b.item) - getUpdatedTime(a.treatment, a.item)
      );
  }, [examinationData]);

  const visibleRows = useMemo(() => {
    const rows: TreatmentRow[] = [];

    treatmentGroups.forEach(({ rowKey, item, treatment, subTreatments }) => {
      rows.push({
        key: rowKey,
        item,
        treatment,
        isSub: false,
      });

      if (expanded[rowKey] && subTreatments.length > 0) {
        subTreatments.forEach((subTreatment: any, subIndex: number) => {
          rows.push({
            key: `${rowKey}-sub-${subTreatment?._id ?? subIndex}`,
            item,
            treatment,
            subTreatment,
            isSub: true,
          });
        });
      }
    });

    return rows;
  }, [treatmentGroups, expanded]);

  const toggleExpanded = (rowKey: string) => {
    setExpanded((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  if (loading) return <SummaryLoading theme={theme} />;

  if (treatmentGroups.length === 0) {
    return <SummaryEmpty message="No treatment history." theme={theme} />;
  }

  const head = summaryTableHead(theme);
  const cell = summaryTableCell(theme);

  return (
    <SummaryTableWrap>
      <table className="w-full min-w-[760px] border-separate border-spacing-0">
        <thead>
          <tr>
            <th className={`${head} w-10 rounded-tl-lg`} aria-label="Expand" />
            <th className={head}>Date</th>
            <th className={head}>Treatment</th>
            <th className={`${head} text-right`}>Amount</th>
            <th className={head}>Note</th>
            <th className={head}>Dentist</th>
            <th className={head}>Status</th>
            <th className={`${head} text-center rounded-tr-lg`}>View</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ key, item, treatment, subTreatment, isSub }, index) => {
            const entry = isSub ? subTreatment : treatment;
            const dentist = entry?.didBy
              ? `${entry.didBy.firstName || ""} ${entry.didBy.lastName || ""}`.trim()
              : "";
            const name = formatTreatmentLabel(entry);
            const amount = formatAmount(entry);
            const status = entry?.status;
            const isLast = index === visibleRows.length - 1;
            const subTreatments = Array.isArray(treatment?.subTreatment) ? treatment.subTreatment : [];
            const hasSubTreatments = !isSub && subTreatments.length > 0;
            const isExpanded = Boolean(expanded[key]);

            return (
              <tr
                key={key}
                className={`border-t ${
                  isSub
                    ? theme === "dark"
                      ? "border-slate-700/50 bg-amber-950/20 hover:bg-amber-950/30"
                      : "border-slate-100 bg-amber-50/40 hover:bg-amber-50/70"
                    : theme === "dark"
                      ? "border-slate-700/50 hover:bg-slate-800/40"
                      : "border-slate-100 hover:bg-slate-50/80"
                }`}
              >
                <td className={`${cell} text-center ${isLast ? "rounded-bl-lg" : ""}`}>
                  {hasSubTreatments ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(key);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Hide sub treatments" : "Show sub treatments"}
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                        theme === "dark"
                          ? "text-amber-300 hover:bg-amber-500/20"
                          : "text-amber-700 hover:bg-amber-100"
                      }`}
                    >
                      {isExpanded ? (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      )}
                    </button>
                  ) : isSub ? (
                    <span className="inline-block w-8" aria-hidden />
                  ) : null}
                </td>
                <td className={cell}>{formatEntryDate(entry)}</td>
                <td
                  className={`${cell} ${isSub ? "pl-10" : ""} ${
                    isSub
                      ? theme === "dark"
                        ? "text-amber-200"
                        : "text-amber-900"
                      : `font-medium ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`
                  }`}
                >
                  {isSub ? (
                    `└ ${name || "—"}`
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="min-w-0">{name || "—"}</span>
                      {hasSubTreatments ? (
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            theme === "dark"
                              ? "bg-amber-500/20 text-amber-300"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {subTreatments.length}
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td
                  className={`${cell} text-right tabular-nums font-semibold ${
                    amount == null
                      ? theme === "dark"
                        ? "text-slate-500"
                        : "text-slate-400"
                      : theme === "dark"
                        ? "text-emerald-300"
                        : "text-emerald-700"
                  }`}
                >
                  {formatAmountLabel(amount)}
                </td>
                <td className={`${cell} max-w-[160px] truncate`}>
                  {hasDisplayValue(entry?.note) ? entry.note : "—"}
                </td>
                <td className={cell}>{dentist || "—"}</td>
                <td className={cell}>{status ? <StatusBadge status={status} theme={theme} /> : "—"}</td>
                <td className={`${cell} text-center ${isLast ? "rounded-br-lg" : ""}`}>
                  {!isSub ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item?._id) {
                          navigate(`/client/treatment-diagnose/add-treatment/diagnose/${item._id}`);
                        }
                      }}
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                        theme === "dark"
                          ? "text-indigo-300 hover:bg-indigo-500/20"
                          : "text-indigo-600 hover:bg-indigo-50"
                      }`}
                      title="Open treatment"
                    >
                      <VisibilityIcon sx={{ fontSize: 17 }} />
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SummaryTableWrap>
  );
};

export default ExaminationTab;
